import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import HorizontalScroller from 'core-system/HorizontalScroller'
import HorizontalScrollerUtils from 'core-system/HorizontalScroller/HorizontalScrollerUtils'
import Bus from 'core-system/Icons/modeTypes/Bus'
import Transit from 'core-system/Icons/modeTypes/Transit'
import Car from 'core-system/Icons/modeTypes/Car'
import FlexContainer from 'core-system/FlexContainer'
import ColorPill from 'core-system/Pill/ColorPill'
import CarbonChange from '../components/CarbonChange'
import {
  getEmployeeSavings,
  getEmployerSavings,
} from 'features/ProgramRecommender/cityCost'
import { formatNumber } from '../Utils'

const MainContainer = styled.div`
  margin-bottom: 1.5rem;
`

const ServicesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  color: ${(props) => props.theme.palette.text.primary};
`

const ScrollableServicesContainer = styled.div`
  max-width: 300px;
  overflow-x: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`

const ReachContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

// Get micromobility providers from redux state
const getMicromobilityProviders = (
  selectedWorksiteCity: string,
  merchants: any[],
  merchantsMap: any
) => {
  if (!merchants || !merchantsMap) return []

  const cityMicromobilityProviders = {
    'san francisco': ['Bay Wheels', 'Lime', 'Spin'],
    'new york': ['Citi Bike'],
    seattle: ['Lime', 'Bird'],
    'other (default)': ['Bay Wheels', 'Lime', 'Spin', 'Citi Bike', 'Bird'],
  }

  // Filter for micromobility providers (bikes, scooters, mopeds)
  const micromobilityModeTypes = [
    'bike',
    'scooter',
    'moped',
    'ebike',
    'escooter',
    'emoped',
    'bikeshare',
    'scootershare',
    'mopedshare',
    'ebikeshare',
    'escootershare',
    'emopedshare',
  ]

  const micromobilityMerchants = merchants.filter((merchant) =>
    merchant.modeTypes.some((modeType: string) => {
      // Filtering the merchants if selectedWorksiteCity is in cityMicromobilityProviders
      if (selectedWorksiteCity.toLowerCase() in cityMicromobilityProviders) {
        return cityMicromobilityProviders[
          selectedWorksiteCity.toLowerCase()
        ].includes(merchant.name)
      }
      return micromobilityModeTypes.includes(modeType.toLowerCase())
    })
  )

  return micromobilityMerchants.map((merchant) => merchant.id)
}

const CommuteAnalysisTable = (props: {
  carReach: number
  micromobilityReach: number
  transitReach: number
  selectedWorksiteCity?: string
  cityCosts: {
    transit: number
    micromobility: number
    car: number
    walking: number
    transitPassType: 'monthly' | 'daily'
    micromobilityPassType: 'monthly' | 'daily'
    carPassType: 'monthly' | 'daily'
  }
  carbonImpact: {
    carImpact: number
    micromobilityImpact: number
    transitImpact: number
  }
  numberOfEmployees: number
}) => {
  const {
    carReach,
    micromobilityReach,
    transitReach,
    selectedWorksiteCity,
    cityCosts,
    carbonImpact,
    numberOfEmployees,
  } = props

  // Get merchants from redux state
  const merchants = useSelector((state: AppState) => state.programs.merchants)
  const merchantsMap = useSelector(
    (state: AppState) => state.programs.merchantsMap
  )

  // Get micromobility providers
  const micromobilityProviderIds = getMicromobilityProviders(
    selectedWorksiteCity,
    merchants,
    merchantsMap
  )

  // Calculate percentage change compared to car (baseline) - per employee
  const calculateCarbonChange = (
    modeCarbon: number,
    carCarbon: number,
    modeReach: number,
    carReach: number
  ) => {
    if (carCarbon === 0 || carReach === 0) return 0

    // Calculate per-employee carbon for each mode
    const modeEmployees = (modeReach / 100) * numberOfEmployees
    const carEmployees = (carReach / 100) * numberOfEmployees

    const perEmployeeModeCarbon =
      modeEmployees > 0 ? modeCarbon / modeEmployees : 0
    const perEmployeeCarCarbon = carEmployees > 0 ? carCarbon / carEmployees : 0

    if (perEmployeeCarCarbon === 0) return 0
    return (
      ((perEmployeeModeCarbon - perEmployeeCarCarbon) / perEmployeeCarCarbon) *
      100
    )
  }

  const getChangeVariant = (change: number) => {
    if (change > 0) return 'negative' // Higher carbon than car = bad
    if (change < 0) return 'positive' // Lower carbon than car = good
    return 'neutral'
  }

  const formatChangeText = (change: number, carCarbon: number) => {
    return `${change > 0 ? '+' : ''}${change.toFixed(0)}% emissions`
  }

  const headerCells = [
    <TableRowCell key='worksite'>
      <Text variant='action4' fontWeight='600'>
        Per Worksite
      </Text>
    </TableRowCell>,
    <TableRowCell key='reach'>
      <Text variant='action4' fontWeight='600'>
        Reach
      </Text>
    </TableRowCell>,
    <TableRowCell key='cost'>
      <Text variant='action4' fontWeight='600'>
        Max Cost Per Employee / mo
      </Text>
    </TableRowCell>,
    <TableRowCell key='carbonImpact'>
      <Text variant='action4' fontWeight='600'>
        Carbon Impact (Kg CO2e) / yr
      </Text>
    </TableRowCell>,
    <TableRowCell key='employerSavings'>
      <Text variant='action4' fontWeight='600'>
        Employer Tax Savings / yr
      </Text>
    </TableRowCell>,
    <TableRowCell key='employeeSavings'>
      <Text variant='action4' fontWeight='600'>
        Employee Tax Savings / yr
      </Text>
    </TableRowCell>,
  ]

  const getReachColor = (percentage: number) => {
    if (percentage <= 25) return 'red'
    if (percentage <= 50) return 'yellow'
    return 'green'
  }

  const commuteModes = [
    {
      name: 'Transit',
      reach: transitReach,
      reachDisplay: transitReach + '%',
      cost: cityCosts.transit,
      passType: cityCosts.transitPassType,
      services: (
        <ServicesContainer>
          <IconContainer>
            <IconWrapper>
              <Bus />
            </IconWrapper>
            <IconWrapper>
              <Transit />
            </IconWrapper>
          </IconContainer>
        </ServicesContainer>
      ),
    },
    {
      name: 'Micromobility',
      reach: micromobilityReach,
      reachDisplay: micromobilityReach + '%',
      cost: cityCosts.micromobility,
      passType: cityCosts.micromobilityPassType,
      services: (
        <ServicesContainer>
          <ScrollableServicesContainer>
            {micromobilityProviderIds.length > 0 ? (
              <HorizontalScroller
                translationPerClick={160}
                marginTop='0'
                resetOnChildrenUpdate
              >
                {HorizontalScrollerUtils.getProviders(
                  micromobilityProviderIds,
                  merchantsMap,
                  undefined,
                  false,
                  false
                )}
              </HorizontalScroller>
            ) : (
              <Text variant='action4'>Scooter, Bike Share</Text>
            )}
          </ScrollableServicesContainer>
        </ServicesContainer>
      ),
    },
    {
      name: 'Car',
      reach: carReach,
      reachDisplay: carReach + '%',
      cost: cityCosts.car,
      passType: cityCosts.carPassType,
      services: (
        <ServicesContainer>
          <IconContainer>
            <IconWrapper>
              <Car />
            </IconWrapper>
          </IconContainer>
        </ServicesContainer>
      ),
    },
  ]

  // Calculate totals
  const totalCarbon = commuteModes.reduce((sum, mode) => {
    const modeKey = `${mode.name.toLowerCase()}Impact`
    return sum + (carbonImpact[modeKey] || 0)
  }, 0)
  let totalEmployerSavings = 0
  let totalEmployeeSavings = 0

  const rows = commuteModes.map((mode, index) => {
    const employeeSavings = getEmployeeSavings(
      mode.cost,
      mode.reach,
      numberOfEmployees
    )
    const employerSavings = getEmployerSavings(
      mode.cost,
      mode.reach,
      numberOfEmployees
    )

    // For micromobility, show dash instead of calculated values
    const displayEmployeeSavings =
      mode.name === 'Micromobility'
        ? '-'
        : `$${formatNumber(Number(employeeSavings))}`
    const displayEmployerSavings =
      mode.name === 'Micromobility'
        ? '-'
        : `$${formatNumber(Number(employerSavings))}`

    // Only add to totals if not micromobility
    if (mode.name !== 'Micromobility') {
      totalEmployeeSavings += Number(employeeSavings)
      totalEmployerSavings += Number(employerSavings)
    }

    totalEmployeeSavings = Number(totalEmployeeSavings)
    totalEmployerSavings = Number(totalEmployerSavings)

    const getModeName = (modeName: string) => {
      if (modeName === 'Car') {
        return 'Driving'
      }
      return modeName
    }

    // Calculate carbon change for transit and micromobility only
    const modeCarbon = carbonImpact[`${mode.name.toLowerCase()}Impact`]
    const carCarbon = carbonImpact.carImpact
    const carbonChange =
      mode.name !== 'Car'
        ? calculateCarbonChange(modeCarbon, carCarbon, mode.reach, carReach)
        : 0
    const changeVariant =
      mode.name !== 'Car' ? getChangeVariant(carbonChange) : 'neutral'
    const changeText =
      mode.name !== 'Car' ? formatChangeText(carbonChange, carCarbon) : ''

    return (
      <TableRow key={index}>
        <TableRowCell>
          <Text variant='action4'>{getModeName(mode.name)}</Text>
        </TableRowCell>
        <TableRowCell>
          <ReachContainer>
            <ColorPill
              color={getReachColor(mode.reach)}
              text={mode.reachDisplay}
            />
          </ReachContainer>
        </TableRowCell>
        <TableRowCell>
          <FlexContainer alignItems='center' gap='0.5rem'>
            <Text variant='action4' fontWeight='700' marginRight='0.5rem'>
              ${formatNumber(mode.cost)}
            </Text>
            <ColorPill
              color={mode.passType === 'monthly' ? 'purple' : 'gray'}
              text={
                mode.passType === 'monthly' ? 'Monthly Pass' : 'Daily Price'
              }
            />
          </FlexContainer>
        </TableRowCell>
        <TableRowCell>
          <CarbonChange
            title={`${formatNumber(carbonImpact[`${mode.name.toLowerCase()}Impact`])}`}
            change={changeText}
            variant={changeVariant}
            titleVariant='action4'
            showChange={mode.name !== 'Car'}
          />
        </TableRowCell>
        <TableRowCell>
          <Text variant='action4' fontWeight='700'>
            {displayEmployerSavings}
          </Text>
        </TableRowCell>
        <TableRowCell>
          <Text variant='action4' fontWeight='700'>
            {displayEmployeeSavings}
          </Text>
        </TableRowCell>
      </TableRow>
    )
  })

  // Add total row
  const totalRow = (
    <TableRow
      key='total'
      style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
    >
      <TableRowCell>
        <Text variant='action4' fontWeight='700'>
          Total
        </Text>
      </TableRowCell>
      <TableRowCell></TableRowCell>
      <TableRowCell></TableRowCell>
      <TableRowCell>
        <Text variant='action4' fontWeight='700'>
          {formatNumber(totalCarbon)}
        </Text>
      </TableRowCell>
      <TableRowCell>
        <Text variant='action4' fontWeight='700'>
          ${formatNumber(totalEmployerSavings)}
        </Text>
      </TableRowCell>
      <TableRowCell>
        <Text variant='action4' fontWeight='700'>
          ${formatNumber(totalEmployeeSavings)}
        </Text>
      </TableRowCell>
    </TableRow>
  )

  return (
    <Table
      headerCells={headerCells}
      rows={[...rows, totalRow]}
      showPagination={false}
      maxRows={10}
    />
  )
}

interface MapStatsProps {
  carReach: number
  micromobilityReach: number
  transitReach: number
  getCityCosts: (
    city: string,
    daysInOffice: number
  ) => {
    transit: number
    micromobility: number
    car: number
    transitPassType: 'monthly' | 'daily'
    micromobilityPassType: 'monthly' | 'daily'
    carPassType: 'monthly' | 'daily'
  }
  selectedWorksiteCity: string
  daysInOffice: number
  numberOfEmployees: number
  carbonImpact: {
    carImpact: number
    micromobilityImpact: number
    transitImpact: number
  }
}

const MapStats: React.FC<MapStatsProps> = (props) => {
  const {
    carReach,
    micromobilityReach,
    transitReach,
    getCityCosts,
    selectedWorksiteCity = 'Other (Default)',
    numberOfEmployees = 0,
    daysInOffice,
    carbonImpact,
  } = props

  const cityCosts = getCityCosts(selectedWorksiteCity, daysInOffice)

  return (
    <MainContainer>
      <CommuteAnalysisTable
        carReach={carReach}
        micromobilityReach={micromobilityReach}
        transitReach={transitReach}
        selectedWorksiteCity={selectedWorksiteCity}
        cityCosts={cityCosts}
        carbonImpact={carbonImpact}
        numberOfEmployees={numberOfEmployees}
      />
    </MainContainer>
  )
}

export default MapStats
